import React, { useCallback } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { OccurrenceEventCard } from './OccurrenceEventCard';
import { CalendarEvent, EventColorSet } from 'pages/Agenda/utils';

interface OccurrenceDraggableEventProps {
  event: CalendarEvent;
  onHover: (data: { event: CalendarEvent; rect: DOMRect; colorSet: EventColorSet }) => void;
  onLeave: () => void;
  onCardClick: (event: CalendarEvent, colorSet: EventColorSet) => void;
  isDragging: boolean;
  colorSet: EventColorSet;
  isCompletedView: boolean;
}

export const OccurrenceDraggableEvent: React.FC<OccurrenceDraggableEventProps> = ({
  event,
  onHover,
  onLeave,
  onCardClick,
  isDragging,
  colorSet,
  isCompletedView,
}) => {
  // Dragging is disabled for occurrences (scenario 09)
  const { setNodeRef } = useDraggable({ id: event.id, disabled: true });

  const handleClick = useCallback(() => {
    if (isDragging) return;
    onCardClick(event, colorSet);
  }, [isDragging, event, colorSet, onCardClick]);

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isDragging) return;
      const rect = e.currentTarget.getBoundingClientRect();
      onHover({ event, rect, colorSet });
    },
    [isDragging, event, colorSet, onHover],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isDragging) return;
      const rect = e.currentTarget.getBoundingClientRect();
      onHover({ event, rect, colorSet });
    },
    [isDragging, event, colorSet, onHover],
  );

  return (
    <div
      ref={setNodeRef}
      data-event-id={event.id}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={onLeave}
      style={{ cursor: 'pointer', width: '100%', minWidth: 0 }}
    >
      <OccurrenceEventCard
        event={event}
        colorSet={colorSet}
        isCompletedView={isCompletedView}
        onEventClick={onCardClick}
      />
    </div>
  );
};
